// Per-item completion sources for setup_checklist_completed's counters.
// GET /academy/setup serves flags only, and the checklist takes days, so the
// source is recorded per coach in localStorage the moment this browser sees
// a row complete (see actions/academySetup) and tallied when the last one
// lands. A row this browser never saw complete has no CTA behind it that we
// know of, so it counts as organic.
import { localGet, localSet } from "app/utils/LocalStorage";
import {
  AcademySetupCompletionSource,
  AcademySetupItem,
  AcademySetupItemKey,
} from "./items";

export type CompletionSources = Partial<
  Record<AcademySetupItemKey, AcademySetupCompletionSource>
>;

export interface CompletionSourceCounts {
  cta_completions: number;
  rail_completions: number;
  organic_completions: number;
}

/** Per coach, like the celebration flag: a shared browser must not mix accounts. */
const storageKey = (userId: string | number) =>
  `academy-setup-completion-sources-${userId}`;

export const readCompletionSources = (
  userId: string | number,
): CompletionSources => {
  const stored = localGet(storageKey(userId));
  return stored && typeof stored === "object" && !Array.isArray(stored)
    ? stored
    : {};
};

export const recordCompletionSources = (
  userId: string | number,
  sources: CompletionSources,
) => {
  localSet(storageKey(userId), {
    ...readCompletionSources(userId),
    ...sources,
  });
};

export const completionSourceCounts = (
  items: AcademySetupItem[],
  sources: CompletionSources,
): CompletionSourceCounts => {
  const counts: CompletionSourceCounts = {
    cta_completions: 0,
    rail_completions: 0,
    organic_completions: 0,
  };
  items
    .filter((item) => item.complete)
    .forEach((item) => {
      const source = sources[item.key] || "organic_nav";
      if (source === "checklist_cta") {
        counts.cta_completions += 1;
      } else if (source === "rail_cta") {
        counts.rail_completions += 1;
      } else {
        counts.organic_completions += 1;
      }
    });
  return counts;
};
